import React, { useCallback, useState } from 'react';
import { NativeModules, StyleSheet } from 'react-native';
import {
  Button,
  IllusAppleWallet,
  Image,
  logger as LOG,
  theme,
  useNoticeBannersStackContext,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useCustomerPassQuery } from './GraphQL/ScanToPay.generated';

// ────────────────────────────────────────────────────────────────────────────────
LOG.enable('APPLE_WALLET');
const logger = LOG.extend('APPLE_WALLET');

// Refer to README at "libs/order/native-modules/modules/sg-apple-wallet-module/README.md".
export const ScanToPayCustomerPass = () => {
  const { t } = useLocalizationContext();
  const [isAddingPass, setIsAddingPass] = useState(false);
  const { url, authToken, hasError, isCustomerPassLoaded } = useCustomerPass();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const handleAddToAppleWallet = useCallback(async () => {
    logger.info('Adding to Apple Wallet', url, authToken);

    setIsAddingPass(true);

    try {
      const response = await NativeModules.AppleWalletModule?.addCustomerPass(
        url,
        authToken,
      );

      // Response is "EXISTING" or "SHOWN", won't know if the customer chose to add it.
      logger.info('Successful response', response);

      // Let the customer know that the pass is already in their wallet.
      if (response === RESPONSE_EXISTING) {
        addNoticeBanner({
          text: t('home.scan-to-pay.pass-existing'),
          palette: 'success',
        });
      }
    } catch (error: unknown) {
      logger.error('Failed to add to Apple Wallet', error);
      addNoticeBanner({
        text: t('home.scan-to-pay.pass-failure'),
        palette: 'caution',
      });
    }

    setIsAddingPass(false);
  }, [url, authToken, t, addNoticeBanner]);

  return (
    <Button
      size="large"
      testID="add-to-apple-wallet"
      style={styles.button}
      disabled={hasError}
      isLoading={!isCustomerPassLoaded || isAddingPass}
      onPress={handleAddToAppleWallet}
    >
      <Image
        style={styles.illustration}
        source={IllusAppleWallet}
        accessibilityIgnoresInvertColors
      />
    </Button>
  );
};

/**
 * Returns the customer pass url and authentication token.
 * Note that the sandbox/development response requires an apple identifier that we do not use.
 * If you wish to test in sandbox/development, please use values from the production response.
 * Here are some examples:
 * https://api.thelevelup.com/passbook/v1/passes/pass.com.sweetgreen.sweetgreen/ab1e51e035f1013b7f2b5ecce166afd7
 * 35e0ce0cec98f7076dba8472c519ca8ac5ba9fefc4914bcd3bdea756305ffe47
 */
const useCustomerPass = () => {
  const [response] = useCustomerPassQuery({
    requestPolicy: 'network-only',
  });

  const url = response.data?.customerPass?.url;
  const authToken = response.data?.customerPass?.authenticationToken;
  const hasError = Boolean(response.error);
  const isCustomerPassLoaded = Boolean(url && authToken) || hasError;

  return { url, authToken, hasError, isCustomerPassLoaded };
};

const RESPONSE_EXISTING = 'EXISTING';

const styles = StyleSheet.create({
  button: {
    backgroundColor: theme.colors.BLACK,
    paddingTop: theme.spacing.small,
  },
  illustration: {
    width: 125,
    height: 28,
  },
});
